import { Injectable, Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { values } from '@sama/micro-dash';
import { filter, take } from 'rxjs/operators';
import { DebouncedEventTracker } from '../../../../cross-team-shared/event-tracking/debounced-event-tracker';
import { buildTrackerFromShellConnector } from '../../../../cross-team-shared/event-tracking/event-utils';
import { addPoints } from '../../../../shared/misc-utilities/point-utils';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';
import {
  PartialRawMask,
  RasterOverlayState,
} from '../../../shared/state/abstract-sensor-content-state-2d';
import { WHITE_CODE } from '../../state/image-content-state';
import { isPointInLayerXY, newMaskForLayer } from '../../utils/mask-utils';
import { AbstractRasterCodingTool } from '../abstract-raster-coding-tool';
import { PaintbrushDragHandler } from './paintbrush-drag-handler';
import { PaintbrushSecondaryToolbarComponent } from './paintbrush-secondary-toolbar.component';

@Injectable()
export class PaintbrushTool extends AbstractRasterCodingTool {
  readonly name = 'Paintbrush';
  override secondaryToolbarType = PaintbrushSecondaryToolbarComponent;

  private _brushWidth = 3;
  private brushSizeEventTracker: DebouncedEventTracker;
  private previousPartialRawMask?: PartialRawMask;

  constructor(injector: Injector) {
    super(injector);
    this.brushSizeEventTracker = new DebouncedEventTracker(
      { id: 'change_paintbrush_size', bucket: 'annotation' },
      buildTrackerFromShellConnector(this.shellConnector),
    );
  }

  get brushWidth() {
    return this._brushWidth;
  }

  set brushWidth(value: number) {
    if (value === this._brushWidth) {
      return;
    }

    this._brushWidth = value;
    this.brushSizeEventTracker.trackWithDebounce({ size: value });
  }

  override async onActivate() {
    await super.onActivate();
    this.subscribeToWhileActive(
      this.store('mouse')('imagePoint').$.pipe(filter(isDefined)),
      this.paintIndicatorAt,
    );
    this.onEvent('container', 'mousedown', this.startPainting);
    this.onEvent('container', 'mousedown', this.startPainting, {
      modifiers: ['altKey'],
    });

    if (this.store.state().layerSettings.zoomPanelEnabled) {
      this.subscribeToWhileActive(
        this.store('hasFocus').$.pipe(
          filter((hasFocus) => hasFocus),
          take(1),
        ),
        () => this.shellConnector.openZoomPanel(),
      );
    }
  }

  override onDeactivate() {
    super.onDeactivate();
    this.clearOverlay();
  }

  paintBrushAt(point: Point2D, store: Store<PartialRawMask>, code: number) {
    const boundingPoints = this.applyBrushWidthToPoint(point);
    const imageSize = this.store.state().sensorRendererState;

    // if all points are outside the image, paint nothing
    if (
      values(boundingPoints).every((p) => !isPointInLayerXY(...p, imageSize))
    ) {
      return undefined;
    }

    const partialRawMask = this.createPartialRawMaskFromBoundingPoints(
      boundingPoints,
      imageSize,
      code,
    );

    store.set(partialRawMask);

    return partialRawMask;
  }

  private startPainting(initialEvent: MouseEvent) {
    this.clearOverlay();
    const handler = new PaintbrushDragHandler(
      this.getCode(initialEvent),
      initialEvent,
      this.injector,
    );

    handler.drag();
    this.registerHelper(handler);
    void handler.completion.finally(() => {
      this.paintIndicatorAt(this.getImagePoint());
    });
  }

  private paintIndicatorAt(point: Point2D) {
    const overlayStore = this.store.getRasterOverlayStore();
    this.clearPreviousIndicator(overlayStore);

    this.previousPartialRawMask = this.paintBrushAt(
      point,
      overlayStore('partialRawMask'),
      this.store.state().selectedTagOptionCode || WHITE_CODE,
    );
  }

  private clearPreviousIndicator(overlayStore: Store<RasterOverlayState>) {
    if (this.previousPartialRawMask) {
      overlayStore('partialRawMask').set({
        ...this.previousPartialRawMask,
        data: new Uint8Array(this.previousPartialRawMask.data.length),
      });
      this.previousPartialRawMask = undefined;
    }
  }

  // Calculate the 4 corners of the brush. All the floor/ceil logic is to handle
  // even number brush width
  private applyBrushWidthToPoint(point: Point2D) {
    const externalRadius = (this.brushWidth - 1) / 2;

    return {
      topLeft: addPoints(point, [
        -Math.ceil(externalRadius),
        -Math.ceil(externalRadius),
      ]),
      topRight: addPoints(point, [
        -Math.floor(externalRadius),
        Math.ceil(externalRadius),
      ]),
      bottomLeft: addPoints(point, [
        Math.ceil(externalRadius),
        -Math.floor(externalRadius),
      ]),
      bottomRight: addPoints(point, [
        Math.floor(externalRadius),
        Math.floor(externalRadius),
      ]),
    };
  }

  private createPartialRawMaskFromBoundingPoints(
    { topLeft, bottomRight }: { topLeft: Point2D; bottomRight: Point2D },
    imageSize: { width: number; height: number },
    code: number,
  ) {
    // fix me if you change this code
    // eslint-disable-next-line no-param-reassign -- fix me!
    topLeft = this.getNearestPointInImage(topLeft, imageSize);
    // fix me if you change this code
    // eslint-disable-next-line no-param-reassign -- fix me!
    bottomRight = this.getNearestPointInImage(bottomRight, imageSize);

    const width = bottomRight[0] - topLeft[0] + 1;
    const height = bottomRight[1] - topLeft[1] + 1;
    const xOffset = topLeft[0];
    const yOffset = topLeft[1];

    let newPartialMask = new Uint8Array(width * height).fill(code);
    if (this.store.state().lockMask && code !== 0) {
      let index = yOffset * imageSize.width + xOffset;
      newPartialMask = newPartialMask.map((value, partialIndex) => {
        if (partialIndex > 0 && partialIndex % width === 0) {
          index += imageSize.width - width;
        }
        const currentColor = this.store.getRawMask()[index];
        if (currentColor !== 0) {
          // fix me if you change this code
          // eslint-disable-next-line no-param-reassign -- fix me!
          value = currentColor;
        }
        index++;
        return value;
      });
    }
    return { xOffset, yOffset, width, height, data: newPartialMask };
  }

  private clearOverlay() {
    const overlayStore = this.store.getRasterOverlayStore();
    overlayStore('rawMask').set(
      newMaskForLayer(this.store.state().sensorRendererState),
    );
  }

  private getNearestPointInImage(
    point: Point2D,
    { width, height }: { width: number; height: number },
  ): Point2D {
    return [
      Math.min(Math.max(point[0], 0), width - 1),
      Math.min(Math.max(point[1], 0), height - 1),
    ];
  }
}
